import React, { Suspense } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate, useLocation } from 'react-router-dom';
import ShiftMonitor from '../Shifts/ShiftMonitor';
import ScrapModal from '../Shifts/ScrapModal';
import { confirm } from '../common/confirm';
import Tip from '../common/Tip';

/* ---- Minimal inline icon set (stroke, currentColor) ---- */
const Icon = ({ name, className = 'w-[18px] h-[18px]' }) => {
    const paths = {
        dashboard: 'M4 13h6V4H4v9zm0 7h6v-5H4v5zm10 0h6V11h-6v9zm0-16v5h6V4h-6z',
        entry: 'M12 4v16m8-8H4',
        receive: 'M4 16v2a2 2 0 002 2h12a2 2 0 002-2v-2M12 4v12m0 0l-4-4m4 4l4-4',
        shifts: 'M12 8v4l3 2m6-2a9 9 0 11-18 0 9 9 0 0118 0z',
        history: 'M3 3v5h5M3.05 13A9 9 0 106 5.3L3 8',
        stock: 'M20 7L12 3 4 7m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4',
        bom: 'M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4',
        item: 'M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0H4',
        category: 'M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z',
        audit: 'M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7l-2 2-1-1',
        report: 'M9 17v-6m4 6V7m4 10v-3M5 21h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v14a2 2 0 002 2z',
        rewind: 'M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15',
        jumbo: 'M20 7c0 1.657-3.582 3-8 3S4 8.657 4 7m16 0c0-1.657-3.582-3-8-3S4 5.343 4 7m16 0v10c0 1.657-3.582 3-8 3s-8-1.343-8-3V7',
        film: 'M7 4v16M17 4v16M3 8h4m10 0h4M3 12h18M3 16h4m10 0h4M4 4h16a1 1 0 011 1v14a1 1 0 01-1 1H4a1 1 0 01-1-1V5a1 1 0 011-1z',
        hr: 'M17 20h5v-2a4 4 0 00-3-3.87M9 20H4v-2a4 4 0 013-3.87m6-1.13a4 4 0 10-4-4 4 4 0 004 4z',
        users: 'M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z',
        sun: 'M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z',
        moon: 'M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z',
        logout: 'M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1',
        plus: 'M12 4v16m8-8H4',
        menu: 'M4 6h16M4 12h16M4 18h16',
        close: 'M6 18L18 6M6 6l12 12',
        chevron: 'M19 9l-7 7-7-7',
        collapse: 'M11 19l-7-7 7-7m8 14l-7-7 7-7',
        expand: 'M13 5l7 7-7 7M5 5l7 7-7 7',
        guide: 'M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253',
    };
    return (
        <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d={paths[name] || ''} />
        </svg>
    );
};

/* Brand mark: a squared "F" built from two strokes — reads as a plant floor plan, not a letter in a bubble. */
const BrandMark = ({ className = 'w-6 h-6' }) => (
    <svg className={className} viewBox="0 0 32 32" aria-hidden="true">
        <rect width="32" height="32" rx="7" className="fill-accent" />
        <path d="M10 23V9h12M10 16h8" stroke="white" strokeWidth="3.2" strokeLinecap="square" fill="none" />
    </svg>
);

const Wordmark = () => (
    <div className="flex items-center gap-2 min-w-0">
        <BrandMark />
        <span className="text-[15px] font-semibold tracking-[-0.015em] text-text-primary truncate">FactoryDash</span>
    </div>
);

// Each route wraps its own <Layout>, so the sidebar remounts on every
// navigation. Module-scoped (not component state) so it survives that
// remount and the nav scroll position doesn't reset to the top.
let desktopNavScrollTop = 0;

const Layout = ({ children }) => {
    const { user, shift, logout, endShift, isAdmin } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();
    const [mobileMenuOpen, setMobileMenuOpen] = React.useState(false);
    const [hrOpen, setHrOpen] = React.useState(location.pathname.startsWith('/hr'));
    const [theme, setTheme] = React.useState(
        () => document.documentElement.getAttribute('data-theme') || 'light'
    );
    const [collapsed, setCollapsed] = React.useState(() => {
        try { return localStorage.getItem('fd-sidebar') === 'collapsed'; } catch (e) { return false; }
    });
    const [showScrapModal, setShowScrapModal] = React.useState(false);

    // Callback ref, not an effect: NavBody is an inline component, so React
    // rebuilds the <nav> element on re-renders (not just on navigation) and
    // each rebuild silently resets its scroll to 0. Restoring at attach time
    // covers every rebuild; a one-time mount effect does not.
    const attachDesktopNav = React.useCallback((el) => {
        if (el) el.scrollTop = desktopNavScrollTop;
    }, []);

    const toggleTheme = () => {
        const next = theme === 'dark' ? 'light' : 'dark';
        setTheme(next);
        document.documentElement.setAttribute('data-theme', next);
        try { localStorage.setItem('fd-theme', next); } catch (e) { /* ignore */ }
    };

    const toggleCollapsed = () => {
        setCollapsed(c => {
            const next = !c;
            try { localStorage.setItem('fd-sidebar', next ? 'collapsed' : 'expanded'); } catch (e) { /* ignore */ }
            return next;
        });
    };

    const handleLogout = async (shouldEndShift) => {
        await logout(shouldEndShift);
        navigate('/login');
    };

    const handleLogoutClick = async () => {
        if (shift) {
            const end = await confirm({
                title: 'End your shift before logging out?',
                description: 'Your shift is still active. End it now, or log out and leave it running.',
                confirmText: 'End shift & log out',
                cancelText: 'Log out, keep shift',
            });
            handleLogout(end);
        } else {
            handleLogout(false);
        }
    };

    const handleEndShiftEarly = async () => {
        if (await confirm({ title: 'End your shift early?', description: "You'll record end-of-shift scrap next.", confirmText: 'End shift' })) {
            // Phase 4: capture end-of-shift scrap before the shift is closed.
            setShowScrapModal(true);
        }
    };

    const handleNavClick = (path) => {
        navigate(path);
        setMobileMenuOpen(false);
    };

    const hrItems = [
        { path: '/hr/team', label: 'Team', admin: false, operatorHidden: true },
        { path: '/hr/attendance', label: 'Attendance', admin: false, operatorHidden: false },
        { path: '/hr/payroll', label: 'Payroll', admin: true, operatorHidden: true },
        { path: '/hr/settings', label: 'Settings', admin: true, operatorHidden: true },
    ];

    const navGroups = [
        { items: [{ path: '/admin/dashboard', label: 'Dashboard', icon: 'dashboard', admin: true }] },
        {
            title: 'Operations', items: [
                { path: '/', label: 'Entry', icon: 'entry' },
                { path: '/inventory/receive', label: 'Receive Stock', icon: 'receive' },
                { path: '/shifts', label: 'Shifts', icon: 'shifts' },
                { path: '/history', label: 'History', icon: 'history' },
            ]
        },
        {
            title: 'Inventory', items: [
                { path: '/inventory/stock', label: 'Stock on Hand', icon: 'stock' },
                { path: '/inventory/jumbo', label: 'Jumbo Production', icon: 'jumbo' },
                { path: '/inventory/rewind', label: 'Rewinding', icon: 'rewind' },
                { path: '/admin/stock-audit', label: 'Stock Audit', icon: 'audit', admin: true },
                { path: '/admin/bom', label: 'BOM', icon: 'bom', admin: true },
                { path: '/inventory/reports', label: 'Reports', icon: 'report', admin: true },
            ]
        },
        {
            title: 'Setup', items: [
                { path: '/admin/items', label: 'Item Master', icon: 'item', admin: true },
                { path: '/admin/categories', label: 'Categories', icon: 'category', admin: true },
                { path: '/admin/film-types', label: 'Film Types', icon: 'film', admin: true },
            ]
        },
        {
            title: 'People', items: [
                { type: 'hr', label: 'HR', icon: 'hr' },
                { path: '/admin/users', label: 'Users', icon: 'users', admin: true },
            ]
        },
        {
            title: 'Help', items: [
                { path: '/guide', label: 'Guide', icon: 'guide' },
            ]
        },
    ];

    const itemVisible = (i) => !(i.admin && !isAdmin());
    const visibleHrItems = hrItems.filter(i => {
        if (i.admin && !isAdmin()) return false;
        if (i.operatorHidden && !isAdmin()) return false;
        return true;
    });

    // isCollapsed param lets the mobile drawer always render expanded
    const renderNavItem = (item, idx, isCollapsed) => {
        const base = 'group w-full flex items-center rounded-md text-[13px] transition-colors duration-150 text-left ' +
            (isCollapsed ? 'justify-center px-0 h-9' : 'gap-2.5 px-2.5 h-8');
        const active = 'bg-surface2 text-text-primary font-medium';
        const idle = 'text-text-secondary font-normal hover:bg-surface2/70 hover:text-text-primary';
        const iconCls = (on) => `w-[17px] h-[17px] shrink-0 transition-colors ${on ? 'text-accent' : 'text-text-muted group-hover:text-text-secondary'}`;

        if (item.type === 'hr') {
            if (visibleHrItems.length === 0) return null;
            const hrActive = location.pathname.startsWith('/hr');
            if (isCollapsed) {
                return (
                    <Tip key={`hr-${idx}`} content="HR" side="right">
                        <button aria-label="HR"
                            className={`${base} ${hrActive ? active : idle}`}
                            onClick={() => { setCollapsed(false); setHrOpen(true); }}>
                            <Icon name="hr" className={iconCls(hrActive)} />
                        </button>
                    </Tip>
                );
            }
            return (
                <div key={`hr-${idx}`}>
                    <button className={`${base} justify-between ${hrActive && !hrOpen ? active : idle}`} onClick={() => setHrOpen(o => !o)} aria-expanded={hrOpen}>
                        <span className="flex items-center gap-2.5"><Icon name="hr" className={iconCls(hrActive)} />{item.label}</span>
                        <Icon name="chevron" className={`w-3.5 h-3.5 text-text-muted transition-transform duration-200 ${hrOpen ? 'rotate-180' : ''}`} />
                    </button>
                    {hrOpen && (
                        <div className="ml-[18px] mt-0.5 mb-1 pl-2.5 border-l border-border space-y-px">
                            {visibleHrItems.map(di => {
                                const a = location.pathname === di.path;
                                return (
                                    <button key={di.path}
                                        aria-current={a ? 'page' : undefined}
                                        className={`relative block w-full text-left px-2.5 h-7 rounded-md text-[13px] transition-colors duration-150 ${a ? 'bg-surface2 text-text-primary font-medium before:absolute before:-left-[11px] before:top-1.5 before:bottom-1.5 before:w-px before:bg-accent' : 'text-text-secondary hover:bg-surface2/70 hover:text-text-primary'}`}
                                        onClick={() => handleNavClick(di.path)}>
                                        {di.label}
                                    </button>
                                );
                            })}
                        </div>
                    )}
                </div>
            );
        }
        if (!itemVisible(item)) return null;
        const a = location.pathname === item.path;
        return (
            <Tip key={item.path} content={isCollapsed ? item.label : undefined} side="right">
                <button
                    aria-label={isCollapsed ? item.label : undefined}
                    aria-current={a ? 'page' : undefined}
                    className={`${base} ${a ? active : idle}`}
                    onClick={() => handleNavClick(item.path)}>
                    <Icon name={item.icon} className={iconCls(a)} />
                    {!isCollapsed && <span className="truncate">{item.label}</span>}
                </button>
            </Tip>
        );
    };

    const NavBody = ({ isCollapsed, navRef, onScroll }) => (
        <nav ref={navRef} onScroll={onScroll} aria-label="Main" className="flex-1 overflow-y-auto overflow-x-hidden px-2 py-3 space-y-4">
            {navGroups.map((group, gi) => {
                const rendered = group.items.map((it, ii) => renderNavItem(it, `${gi}-${ii}`, isCollapsed)).filter(Boolean);
                if (rendered.length === 0) return null;
                return (
                    <div key={gi} className="space-y-px">
                        {group.title && (isCollapsed
                            ? <div className="mx-3 my-2 border-t border-border" />
                            : <div className="px-2.5 pb-1 text-[11px] font-medium text-text-muted">{group.title}</div>
                        )}
                        {rendered}
                    </div>
                );
            })}
        </nav>
    );

    const initial = (user?.fullName || '?').charAt(0).toUpperCase();

    const SidebarFooter = ({ isCollapsed }) => (
        <div className="border-t border-border p-2 space-y-1">
            {isCollapsed ? (
                <div className="flex flex-col items-center gap-1">
                    <Tip content={user?.fullName} side="right">
                        <div className="w-8 h-8 rounded-lg bg-accent/10 text-accent ring-1 ring-inset ring-accent/20 flex items-center justify-center text-xs font-semibold">
                            {initial}
                        </div>
                    </Tip>
                    <Tip content={theme === 'dark' ? 'Light mode' : 'Dark mode'} side="right">
                        <button onClick={toggleTheme} className="p-2 rounded-md text-text-secondary hover:bg-surface2 hover:text-text-primary transition-colors" aria-label="Toggle theme">
                            <Icon name={theme === 'dark' ? 'sun' : 'moon'} className="w-[17px] h-[17px]" />
                        </button>
                    </Tip>
                    <Tip content="Log out" side="right">
                        <button onClick={handleLogoutClick} className="p-2 rounded-md text-text-secondary hover:bg-danger/10 hover:text-danger transition-colors" aria-label="Log out">
                            <Icon name="logout" className="w-[17px] h-[17px]" />
                        </button>
                    </Tip>
                </div>
            ) : (
                <>
                    <div className="flex items-center justify-between gap-2 px-1 py-1">
                        <div className="flex items-center gap-2.5 min-w-0">
                            <div className="w-8 h-8 rounded-lg bg-accent/10 text-accent ring-1 ring-inset ring-accent/20 flex items-center justify-center text-xs font-semibold shrink-0">
                                {initial}
                            </div>
                            <div className="min-w-0 leading-tight">
                                <div className="text-[13px] font-medium text-text-primary truncate">{user?.fullName}</div>
                                <div className="text-[11px] text-text-muted capitalize">{user?.role}</div>
                            </div>
                        </div>
                        <Tip content={theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}>
                            <button onClick={toggleTheme} className="p-2 rounded-md text-text-secondary hover:bg-surface2 hover:text-text-primary transition-colors shrink-0" aria-label="Toggle theme">
                                <Icon name={theme === 'dark' ? 'sun' : 'moon'} className="w-[17px] h-[17px]" />
                            </button>
                        </Tip>
                    </div>
                    <button onClick={handleLogoutClick} className="group w-full flex items-center gap-2.5 px-2.5 h-8 rounded-md text-[13px] text-text-secondary hover:bg-danger/10 hover:text-danger transition-colors">
                        <Icon name="logout" className="w-[17px] h-[17px] text-text-muted group-hover:text-danger transition-colors" />Log out
                    </button>
                </>
            )}
        </div>
    );

    return (
        <div className="min-h-screen flex bg-background text-text-primary font-sans">
            <ShiftMonitor />

            {showScrapModal && (
                <ScrapModal
                    onClose={() => setShowScrapModal(false)}
                    onComplete={endShift}
                />
            )}

            {/* ===== Desktop sidebar ===== */}
            <aside className={`hidden md:flex md:flex-col shrink-0 bg-card border-r border-border h-screen sticky top-0 transition-[width] duration-200 ease-out ${collapsed ? 'w-16' : 'w-60'}`}>
                <div className={`h-14 flex items-center shrink-0 ${collapsed ? 'justify-center px-0' : 'justify-between pl-4 pr-2'}`}>
                    {!collapsed && <Wordmark />}
                    <Tip content={collapsed ? 'Expand sidebar' : 'Collapse sidebar'} side="right">
                        <button onClick={toggleCollapsed} className="p-2 rounded-md text-text-muted hover:bg-surface2 hover:text-text-primary transition-colors" aria-label="Toggle sidebar">
                            <Icon name={collapsed ? 'expand' : 'collapse'} className="w-4 h-4" />
                        </button>
                    </Tip>
                </div>
                <NavBody isCollapsed={collapsed} navRef={attachDesktopNav} onScroll={e => { desktopNavScrollTop = e.target.scrollTop; }} />
                <SidebarFooter isCollapsed={collapsed} />
            </aside>

            {/* ===== Mobile drawer ===== */}
            {mobileMenuOpen && (
                <div className="md:hidden fixed inset-0 z-50 flex">
                    <div className="absolute inset-0 bg-black/50 backdrop-blur-[2px] animate-in fade-in duration-150" onClick={() => setMobileMenuOpen(false)} />
                    <div className="relative w-64 max-w-[80%] bg-card border-r border-border flex flex-col h-full shadow-2xl animate-in slide-in-from-left duration-200">
                        <div className="h-14 flex items-center justify-between pl-4 pr-2 shrink-0">
                            <Wordmark />
                            <button onClick={() => setMobileMenuOpen(false)} className="p-2 rounded-md text-text-secondary hover:bg-surface2" aria-label="Close menu">
                                <Icon name="close" className="w-5 h-5" />
                            </button>
                        </div>
                        <NavBody isCollapsed={false} />
                        <SidebarFooter isCollapsed={false} />
                    </div>
                </div>
            )}

            {/* ===== Main column ===== */}
            <div className="flex-1 flex flex-col min-w-0">
                {shift && (
                    <div className="bg-success/[0.08] border-b border-success/20 text-success h-10 px-4 md:px-8 flex justify-between items-center text-[13px] gap-3" role="status">
                        <span className="flex items-center gap-2.5 min-w-0">
                            <span className="fd-live-dot" aria-hidden="true" />
                            <span className="font-medium truncate">
                                <span className="capitalize">{shift.shift_type}</span> shift live
                                <span className="text-success/70 font-normal tnum"> · since {new Date('1970/01/01 ' + shift.start_time).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
                            </span>
                        </span>
                        <button onClick={handleEndShiftEarly} className="h-7 px-2.5 rounded-md border border-success/30 bg-card/60 text-success font-medium hover:bg-card hover:border-success/50 active:translate-y-px transition-colors whitespace-nowrap">
                            End shift
                        </button>
                    </div>
                )}

                <div className="md:hidden h-14 flex items-center justify-between px-4 bg-card/90 backdrop-blur border-b border-border sticky top-0 z-30">
                    <button onClick={() => setMobileMenuOpen(true)} className="p-2 -ml-2 rounded-md text-text-secondary hover:bg-surface2" aria-label="Open menu">
                        <Icon name="menu" className="w-5 h-5" />
                    </button>
                    <Wordmark />
                    <span className="px-1.5 py-0.5 rounded text-[11px] font-medium text-text-secondary bg-surface2 ring-1 ring-inset ring-border capitalize">{user?.role}</span>
                </div>

                <main id="main" className="flex-1 w-full max-w-[1400px] mx-auto px-4 py-5 md:px-8 md:pt-7 md:pb-10">
                    {/* Pages are lazy-loaded (see App.js); show a spinner in the
                        content area only, so the sidebar doesn't flash. */}
                    <Suspense
                        fallback={
                            <div className="flex items-center justify-center py-24" role="status" aria-live="polite">
                                <span className="spinner text-accent" style={{ width: '1.25rem', height: '1.25rem' }} />
                            </div>
                        }
                    >
                        {children}
                    </Suspense>
                </main>
            </div>
        </div>
    );
};

export default Layout;
